/**
 * PATH: apps/qa/src/pages/area-execution/area-execution-types.ts
 * PURPOSE: Types and status mapping for area execution checklist
 */

import type { ItemInstance, Evidence } from '@/types/qa'

/** Kept identical to QaStatus in @quotech/ui and to the backend ItemStatus. */
export type BackendItemStatus =
  | 'not_started'
  | 'in_progress'
  | 'further_action'
  | 'done'
  | 'pass'
  | 'failed'
  | 'not_applicable'
export type UIItemStatus = 'pending' | 'done' | 'failed' | 'na' | 'hold' | 'further_action'

/**
 * Display map for the header chip and advanceToNext.
 * pass reads as Pass (done). further_action is first-class. in_progress stays
 * pending so a half-started item still counts as needing a visit.
 */
export function toUIStatus(backendStatus: BackendItemStatus | string): UIItemStatus {
  const map: Record<string, UIItemStatus> = {
    not_started: 'pending', in_progress: 'pending',
    further_action: 'further_action', pass: 'done',
    done: 'done', failed: 'failed', not_applicable: 'na',
  }
  return map[backendStatus] || 'pending'
}

/**
 * A backend status passes straight through, so the picker can send the two
 * states the three buttons cannot express without a second write path. The old
 * signature silently returned undefined for anything unmapped, which the API
 * layer would have posted as a 400.
 */
export function toBackendStatus(uiStatus: UIItemStatus | BackendItemStatus): BackendItemStatus {
  const map: Record<string, BackendItemStatus> = {
    pending: 'not_started', done: 'done', failed: 'failed', na: 'not_applicable',
    hold: 'not_started', further_action: 'further_action',
    not_applicable: 'not_applicable', pass: 'done',
  }
  return map[uiStatus] ?? (uiStatus as BackendItemStatus)
}

export interface ItemWithState extends ItemInstance {
  localStatus?: UIItemStatus
  localNotes?: string
  isSyncing?: boolean
  evidence?: Evidence[]
}

export interface Template {
  id: string
  name: string
  trade?: string
  item_count?: number
}
